import { Check } from '@mui/icons-material';
import { Button, CircularProgress } from '@mui/material';
import { Formik, FormikConfig, FormikErrors, FormikProps, useFormikContext } from 'formik';
import { noop } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ConfirmationModal } from '../../components/modal/confirmation-modal/ConfirmationModal';
import { notify } from '../../notifications';

export interface FormProps<Values, T = any> extends FormikConfig<Values> {
  readonly initialValues: Values;
  readonly onChange?: (data: Values) => any;
  readonly onSubmit: (data: Values) => Promise<T> | void;
  readonly onSuccess?: (data: T) => any;
  readonly onError?: (data: T) => any;
  readonly onCancel?: () => any;
  readonly useUpdateLabels?: boolean;
  readonly actions?: (
    formikProps: FormikProps<Values>,
    defaultActions: React.ReactNode,
  ) => React.ReactNode;
  readonly allowSave?: boolean;
  readonly confirmCloseIfDirty?: boolean;
  readonly saveLabel?: React.ReactNode;
  readonly savedLabel?: React.ReactNode;
  readonly cancelLabel?: React.ReactNode;
  readonly innerRef?: any;
}

Form.defaultProps = {
  onSuccess: () => {
    noop();
  },
  confirmCloseIfDirty: true,
};

export function Form<Values>(p: FormProps<Values>) {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [hasSucceded, setHasSucceded] = useState(false);

  const DEFAULT_LABELS = {
    saveLabel: <FormattedMessage id="common.form.saveBtnText" defaultMessage="Guardar" />,
    savedLabel: (
      <React.Fragment>
        <Check fontSize="small" />{' '}
        <FormattedMessage id="common.form.savedBtnText" defaultMessage="Guardado" />
      </React.Fragment>
    ),
  };

  const UPDATE_LABELS = {
    saveLabel: <FormattedMessage id="common.form.updateBtnText" defaultMessage="Guardar" />,
    savedLabel: (
      <React.Fragment>
        <Check fontSize="small" />{' '}
        <FormattedMessage id="common.form.updatedBtnText" defaultMessage="Guardado" />
      </React.Fragment>
    ),
  };

  const defaultLabels = p.useUpdateLabels ? UPDATE_LABELS : DEFAULT_LABELS;
  const labels = {
    saveLabel: p.saveLabel || defaultLabels.saveLabel,
    savedLabel: p.savedLabel || defaultLabels.savedLabel,
  };

  return (
    <React.Fragment>
      <Formik
        innerRef={p.innerRef}
        initialValues={useMemo(() => p.initialValues as any, [])}
        validationSchema={p.validationSchema}
        validateOnMount
        onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
          setHasSucceded(false);

          try {
            const res = await p.onSubmit(values);

            if (res?.errors) {
              handleError(res.errors);
              return;
            }

            setHasSucceded(true);
            setSubmitting(false);
            resetForm({
              values,
            });

            p.onSuccess!(res);
          } catch (err: any) {
            console.error('Form error:', err);
            if (p.onError) {
              onError(err, p.onError);
            } else {
              handleError(err.errors || {});
            }
          }

          function onError(err: any, onErrorCallback: (data: any) => void) {
            setErrors(err.errors || {});
            setSubmitting(false);

            if (onErrorCallback) {
              onErrorCallback(err);
            }
          }

          function handleError(errors: FormikErrors<Values>) {
            setErrors(errors);
            setSubmitting(false);

            notify.error(
              <FormattedMessage
                id="common.form.errorNotification"
                defaultMessage="Hubo un error. {br} Verifica e intenta nuevamente."
                values={{
                  br: <br />,
                }}
              />,
            );
          }
        }}
      >
        {(fp) => {
          const disableSave =
            fp.isSubmitting || p.allowSave === false || !!Object.keys(fp.errors).length;
          const saveLabel = fp.isSubmitting
            ? ''
            : hasSucceded && !fp.dirty
              ? labels.savedLabel
              : labels.saveLabel;

          const defaultActions = (
            <div className="mt-3">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => fp.handleSubmit()}
                disabled={disableSave}
                startIcon={
                  fp.isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
                }
                sx={{ minWidth: '8rem' }}
                data-cy="form-save"
              >
                {saveLabel}
              </Button>
              {p.onCancel && (
                <Button
                  className="ml-3"
                  variant="outlined"
                  color="secondary"
                  data-cy="form-cancel"
                  disabled={fp.isSubmitting}
                  onClick={() => {
                    if (!fp.dirty || !p.confirmCloseIfDirty) {
                      return p.onCancel!();
                    }

                    if (!showCancelConfirm) {
                      setShowCancelConfirm(true);
                    }
                  }}
                >
                  <FormattedMessage id="common.form.cancelBtnText" defaultMessage="Cancelar" />
                </Button>
              )}
            </div>
          );

          return (
            <React.Fragment>
              {typeof p.children === 'function' ? p.children(fp) : p.children}
              {p.actions ? p.actions(fp, defaultActions) : defaultActions}
              {p.onChange && <ChangedValuesHandler onChange={p.onChange} />}
            </React.Fragment>
          );
        }}
      </Formik>
      <ConfirmationModal
        show={showCancelConfirm}
        onHide={() => setShowCancelConfirm(false)}
        prompt={
          <FormattedMessage
            id="common.form.exitPrompt"
            defaultMessage={`
              Deseas realmente salir? {br}Cualquier cambio no guardado será perdido.
            `}
            values={{
              br: <br />,
            }}
          />
        }
        onConfirm={p.onCancel!}
        confirmLabel={
          <FormattedMessage
            id="common.form.exitConfirmBtnText"
            defaultMessage="Salir sin guardar"
          />
        }
        cancelLabel={
          <FormattedMessage
            id="common.form.exitCancelBtnText"
            defaultMessage="Continuar editando"
          />
        }
      />
    </React.Fragment>
  );
}

function ChangedValuesHandler({ onChange }: { onChange: (data: any) => any }) {
  const { values } = useFormikContext();

  useEffect(() => {
    onChange(values);
  }, [values]);

  return null;
}
